/**
 * Yahtzee Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService.
 *
 * Follows LudoErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum YahtzeeErrorType {
  INVALID_CATEGORY = 'INVALID_CATEGORY',
  CATEGORY_ALREADY_SCORED = 'CATEGORY_ALREADY_SCORED',
  NO_ROLLS_REMAINING = 'NO_ROLLS_REMAINING',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface YahtzeeError {
  type: YahtzeeErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class YahtzeeErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: YahtzeeError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: YahtzeeErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): YahtzeeError {
    const error: YahtzeeError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Yahtzee', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      const notifType = this.getNotificationType(severity);
      this.notificationService.addNotification({
        type: notifType,
        title: 'Yahtzee',
        message: this.getUserFriendlyMessage(type, message),
        duration: severity === ErrorSeverity.LOW ? 3000 : 5000,
      });
    }

    return error;
  }

  private logError(error: YahtzeeError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private isRecoverable(type: YahtzeeErrorType): boolean {
    switch (type) {
      case YahtzeeErrorType.INVALID_CATEGORY:
      case YahtzeeErrorType.CATEGORY_ALREADY_SCORED:
      case YahtzeeErrorType.NO_ROLLS_REMAINING:
      case YahtzeeErrorType.NOT_YOUR_TURN:
        return true;
      case YahtzeeErrorType.GAME_ENDED:
      case YahtzeeErrorType.CORRUPTED_DATA:
        return false;
      default:
        return true;
    }
  }

  private classifyError(type: YahtzeeErrorType): {
    errorType: ErrorType;
    severity: ErrorSeverity;
  } {
    switch (type) {
      case YahtzeeErrorType.INVALID_CATEGORY:
      case YahtzeeErrorType.CATEGORY_ALREADY_SCORED:
      case YahtzeeErrorType.NO_ROLLS_REMAINING:
      case YahtzeeErrorType.NOT_YOUR_TURN:
      case YahtzeeErrorType.GAME_LOGIC:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };
      case YahtzeeErrorType.CONNECTION_ERROR:
      case YahtzeeErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.NETWORK, severity: ErrorSeverity.MEDIUM };
      case YahtzeeErrorType.CORRUPTED_DATA:
      case YahtzeeErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.HIGH };
      case YahtzeeErrorType.SAVE_ERROR:
      case YahtzeeErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };
      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  private getNotificationType(severity: ErrorSeverity): NotificationType {
    switch (severity) {
      case ErrorSeverity.LOW: return NotificationType.WARNING;
      case ErrorSeverity.MEDIUM: return NotificationType.ERROR;
      case ErrorSeverity.HIGH: return NotificationType.ERROR;
      case ErrorSeverity.CRITICAL: return NotificationType.ERROR;
      default: return NotificationType.WARNING;
    }
  }

  private getUserFriendlyMessage(type: YahtzeeErrorType, fallback: string): string {
    switch (type) {
      case YahtzeeErrorType.INVALID_CATEGORY: return 'Invalid scoring category selected.';
      case YahtzeeErrorType.CATEGORY_ALREADY_SCORED: return 'That category has already been scored.';
      case YahtzeeErrorType.NO_ROLLS_REMAINING: return 'No rolls remaining. Please select a category.';
      case YahtzeeErrorType.NOT_YOUR_TURN: return 'Please wait for your turn.';
      case YahtzeeErrorType.GAME_ENDED: return 'The game has ended.';
      case YahtzeeErrorType.CONNECTION_ERROR: return 'Connection lost. Attempting to reconnect...';
      case YahtzeeErrorType.CORRUPTED_DATA: return 'Game data is corrupted. Starting a new game.';
      default: return fallback;
    }
  }

  getErrorLog(): YahtzeeError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
